/**
 * Register page
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (element: HTMLElement, config: object) => void;
        };
      };
    };
  }
}

export function RegisterPage() {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response) => {
        try {
          await loginWithGoogle(response.credential);
          navigate('/');
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Google sign-in failed');
        }
      }
    });

    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        width: 400
      });
    }
  }, [loginWithGoogle, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await register(username, password, displayName);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: 'var(--radius-sm)',
    transition: 'border-color var(--transition-base)'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--spacing-sm)',
    fontWeight: 'bold',
    color: 'var(--color-text)'
  };

  return (
    <div style={{ maxWidth: '500px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <div className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <h1 style={{ marginTop: 0, marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>Register</h1>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <label htmlFor="username" style={labelStyle}>
              Username (min 3 characters)
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <label htmlFor="password" style={labelStyle}>
              Password (min 6 characters)
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <label htmlFor="displayName" style={labelStyle}>
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              style={inputStyle}
            />
          </div>

          {error && (
            <div style={{
              padding: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-lg)',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: 'var(--radius-sm)',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div style={{ margin: 'var(--spacing-lg) 0', display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e0e0e0' }} />
          <span style={{ color: '#888', fontSize: '0.9rem' }}>or</span>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e0e0e0' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div ref={googleBtnRef} id="google-signin-btn" />
        </div>

        <div style={{ marginTop: 'var(--spacing-lg)', textAlign: 'center' }}>
          <Link to="/login" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
            Already have an account? <strong>Login</strong>
          </Link>
        </div>
      </div>
    </div>
  );
}
